import { ConsolidationStatus, Partner } from '@returnmates/client-core/src/graphql/generated/api'
import { getCarriers, getPartners } from '@returnmates/client-core/src/selectors/admin'
import Input from '@returnmates/ui-core/src/components/Input'
import clsx from 'clsx'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, useField } from 'react-final-form'
import { useSelector } from 'react-redux'

import CalendarIcon from '../../images/icons/calendar'
import SingleDatePicker from '../../SingleDatePicker'
import WrappedSelect from '../../WrappedSelect'
import useStyles from '../styles'

interface Props {
  id: string
}

const Information = ({ id }: Props) => {
  const classes = useStyles()
  const partners = useSelector(getPartners)
  const carriers = useSelector(getCarriers)
  const { input: trackingIdInput } = useField('trackingId')
  const { input: statusInput } = useField('status')
  const { input: carrierInput } = useField('carrier')
  const [isCarrierManuallySelected, setIsCarrierManuallySelected] = useState(false)

  const onChangeCarrier = useCallback(
    val => {
      carrierInput.onChange(val)
      setIsCarrierManuallySelected(true)
    },
    [carrierInput],
  )

  const autoDetectCarrier = useCallback(
    value => {
      const detectedCarrier = carriers?.find(carrier => {
        if (carrier.regex) {
          const regex = new RegExp(carrier.regex)
          return regex.test(value.replace(/ /g, ''))
        }
      })

      carrierInput.onChange(detectedCarrier)
    },
    [carriers, carrierInput],
  )

  const onChangeTrackingId = useCallback(
    val => {
      trackingIdInput.onChange(val)
      if (!isCarrierManuallySelected) {
        autoDetectCarrier(val)
      }
    },
    [autoDetectCarrier, isCarrierManuallySelected, trackingIdInput],
  )

  const generateTrackingId = useCallback(() => {
    const newTrackingId = id
      ?.split('-')
      .join('')
      .toUpperCase()
    trackingIdInput.onChange(newTrackingId)
    autoDetectCarrier(newTrackingId)
  }, [autoDetectCarrier, id, trackingIdInput])

  const trackingAutoFocus = useMemo(() => !trackingIdInput.value, [trackingIdInput.value])

  useEffect(() => {
    if (trackingIdInput && !carrierInput.value) {
      autoDetectCarrier(trackingIdInput.value)
    }
  }, [trackingIdInput, carrierInput.value, autoDetectCarrier])

  useEffect(() => {
    if (carrierInput.value && typeof carrierInput.value === 'object') {
      setIsCarrierManuallySelected(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Created Date</p>
        <Field name="createdAt">
          {({ input, meta }) => (
            <SingleDatePicker
              placeholder="Select date"
              fullWidth
              value={input.value}
              onChange={input.onChange}
              keyboardIcon={CalendarIcon}
              className={classes.datePicker}
              disabled
              error={meta.touched && meta.error}
              helperText={meta.error}
              disablePast
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Expected Departure Date</p>
        <Field name="expectedDeliveryDate">
          {({ input, meta }) => (
            <SingleDatePicker
              placeholder="Select date"
              fullWidth
              value={input.value}
              onChange={input.onChange}
              keyboardIcon={CalendarIcon}
              className={classes.datePicker}
              disabled={statusInput.value === ConsolidationStatus.DEPARTED}
              error={meta.touched && meta.error}
              helperText={meta.error}
              disablePast
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Weight</p>
        <Field name="weight">
          {({ input, meta }) => (
            <Input
              placeholder="Weight"
              classes={{
                textField: classes.input,
              }}
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
              type="number"
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}># of Packages</p>
        <Field name="packageCount">
          {({ input, meta }) => (
            <Input
              classes={{
                textField: classes.input,
              }}
              value={input.value || 0}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
              disabled
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Partner:</p>
        <Field name="partner">
          {({ input }) => (
            <WrappedSelect<'code', Partner>
              value={input.value as Partner}
              onChange={input.onChange}
              values={partners as Partner[]}
              objectKey="code"
              placeholder="Choose partner..."
              fullWidth
              disabled
              popupIconStyles={classes.disableIcon}
            />
          )}
        </Field>
      </div>
      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Carrier:</p>
        <Field name="carrier">
          {({ input }) => (
            <WrappedSelect
              value={input.value}
              onChange={onChangeCarrier}
              values={carriers || []}
              objectKey="id"
              placeholder="Choose carrier"
              fullWidth
            />
          )}
        </Field>
      </div>
      <div className={clsx(classes.detailsRow, classes.trackingIdDetailsRow)}>
        <div className={classes.inputHeadWrapper}>
          <p className={classes.inputHead}>Tracking Id:</p>
          <div className={classes.noLabelButton} onClick={generateTrackingId}>
            Generate Tracking ID
          </div>
        </div>
        <Field name="trackingId">
          {({ input, meta }) => (
            <div className={classes.trackingInput}>
              <Input
                placeholder="Add tracking Id"
                classes={{
                  textField: classes.input,
                }}
                onChange={onChangeTrackingId}
                value={input.value}
                autoFocus={trackingAutoFocus}
                fullWidth
                helperText={meta.error || meta.submitError}
                error={
                  (!meta.touched && meta.error) || (!meta.dirtySinceLastSubmit && meta.submitError)
                }
              />
            </div>
          )}
        </Field>
      </div>
    </>
  )
}

export default memo(Information)
